
import { Constants } from "constants/Constants";
import { ActionResult } from "data/StepWithAction";
import { isFieldsNotEmpty } from "functions/isFieldsNotEmpty";

import { MultiPageGuideCollection } from "../MultiPageGuideCollection";
import { SinglePageGuideCollection } from "../SinglePageGuideCollection";

const delayMs = 1000;

const Step1ConfigureProject: SinglePageGuideCollection = {
    stageNumber: 1,
    cookieName: 'topic-authority-guide-stage-1',
    navigateTo: '/account',
    steps: [
        {
            title: 'Let\'s build topic authority!',
            tooltipClass: 'lg',
            intro: `
            <img src="/topic-authority-starter.gif" alt="intro gif" style="max-width: 100%; display: block; margin:0 auto;" /><br />
            Let's learn how to cast a topic authority spell!
            `,
            onBeforeNextStep: () => {
                // eslint-disable-next-line no-underscore-dangle
                const direction = (window as any).introContext._direction;
                if (direction === 'backward') {
                    document.getElementById('wizardPreviousBtn_2')?.click();
                    setTimeout(() => {
                        (window as any).introContext.refresh();
                    }, delayMs);
                }
            }
        },
        {
            element: '#wizardNextBtn_1',
            intro: 'To begin with, we can skip company information and go right into the project settings by click <strong>Next</strong>',
            disableInteraction: true,
            onBeforeNextStep: () => {
                // eslint-disable-next-line no-underscore-dangle
                const direction = (window as any).introContext._direction;
                if (direction === 'forward') {
                    document.getElementById('wizardNextBtn_1')?.click();
                    setTimeout(() => {
                        (window as any).introContext.refresh();
                    }, delayMs);
                }
            }
        },
        {
            element: '#websiteUrl',
            intro: 'Enter the website URL for your CMS.  Usually this is the same as your main website domain.',
            position: 'top',
            isActionCompleted: (): ActionResult => isFieldsNotEmpty(['#websiteUrl'], 'You must enter a valid website URL to continue!'),
        },
        {
            element: '#gptApiKey',
            intro: 'If you would like to use the latest GPT models, enter your own API key here.  This step is optional.  To find your API key, go to <a href="https://platform.openai.com/account/api-keys" target="_blank">OpenAI Platform</a>.',
            position: 'top'
        },
        {
            element: '#targetAudience',
            intro: 'Enter the name of your target audience.  This will significantly improve the relevance of article ideas!',
            position: 'top',
            isActionCompleted: (): ActionResult => isFieldsNotEmpty(['#targetAudience'], 'You must enter an audience to continue!'),
        },
        {
            element: '#solutionForAudience',
            intro: `<p>Enter a brief explanation of the solution you provide to your audience.</p>
            <div class="alert alert-info">If later you find the relevance of article suggestions is low, try adjusting this explanation.</div>`,
            position: 'top',
            isActionCompleted: (): ActionResult => isFieldsNotEmpty(['#solutionForAudience'], 'You must enter a solution to continue!'),
            onBeforeNextStep: () => {
                setTimeout(() => {
                    (window as any).introContext.refresh();
                }, delayMs);
            }
        },
        {
            element: '#globalNegativeTerms',
            intro: `Add any topics you want to exclude.
                    <div class="alert alert-info">If irrelevant content is generated later, you can add those topics here to prevent them from appearing in future results.</div>`,
            position: 'top',
            onBeforeNextStep: () => {
                // eslint-disable-next-line no-underscore-dangle
                const direction = (window as any).introContext._direction;
                if (direction === 'backward') {
                    document.getElementById('wizardPreviousBtn_3')?.click();
                    setTimeout(() => {
                        (window as any).introContext.refresh();
                    }, delayMs);
                }
            }
        },
        {
            element: '#wizardNextBtn_2',
            intro: 'Click <strong>Next</strong> to save all of your project settings.',
            position: 'top',
            disableInteraction: true,
            onBeforeNextStep: () => {
                // eslint-disable-next-line no-underscore-dangle
                const direction = (window as any).introContext._direction;
                if (direction === 'forward') {
                    document.getElementById('wizardNextBtn_2')?.click();
                } else {
                    document.getElementById('wizardPreviousBtn_2')?.click();
                }
                setTimeout(() => {
                    (window as any).introContext.refresh();
                }, delayMs);
            }
        },
        {
            element: '.ConfigureCms',
            tooltipClass: 'lg',
            intro: `For now we'll skip setting <strong>CMS</strong> and <strong>social media connections</strong>, but <strong style="color: red">remember to configure these later to publish to them automatically</strong>!`,
            disableInteraction: true
        }
    ]
}

const Step2UploadSitemap: SinglePageGuideCollection = {
    stageNumber: 2,
    cookieName: 'topic-authority-guide-stage-2',
    navigateTo: '/seo',
    steps: [

        {
            element: '#sitemapUrl',
            title: 'Step 2: Ingest Your Sitemap',
            tooltipClass: 'xxl',
            intro: `
            <p>Start by finding your WordPress post sitemap URL—usually <code>https://yourwebsite.com/post-sitemap.xml</code>. Open it in a new tab to confirm it works.</p>
<p>If it doesn't work, check <a href="https://wordpress.com/support/sitemaps/" target="_blank">this guide</a> or <a href="https://wordpress.org/support/topic/what-is-the-directory-where-i-find-the-sitemap-file/" target="_blank">this secondary guide</a>.</p>
<div class="alert alert-warning">Only <strong>post</strong> sitemaps are supported, so be sure to use the correct one. Need help? <a href="${Constants.SUPPORT_TICKET_URL}" target="_blank">Submit a support ticket</a>.</div>

            `,
            position: 'bottom-right-aligned',
            isActionCompleted: (): ActionResult => isFieldsNotEmpty(['#sitemapUrl'], 'You must enter a valid post sitemap to continue!'),
        },
        {
            element: '#submitSitemap',
            intro: 'Next, click on <strong>Process Sitemap</strong>!',
            position: 'bottom',
            isActionCompleted: (): ActionResult => {
                const statusElement = document.getElementById('lastSubmitStatus');
                if (!statusElement) {
                    return { isCompleted: false, message: 'You must submit a sitemap to continue!' };
                }
                return { isCompleted: true };
            },
        },
        {
            element: '#crawlStatus',
            tooltipClass: 'xl',
            intro: `
                <p>Continue to <strong>Check crawl status</strong> until the <code>Submission Status</code> is <code>Success</code>.  
                If this fails, you may click back and retry.</p>
                <p>Typically this only takes a few minutes, but it may depend on how big your website is.</p>`,
            position: 'left',
            isActionCompleted: (): ActionResult => {
                const statusElement = document.getElementById('lastSubmitStatus');
                if (!statusElement) {
                    return { isCompleted: false, message: 'You must submit a sitemap to continue!' };
                }
                const hasSuccessStatus = statusElement.innerText.toLowerCase() === 'success';
                if (!hasSuccessStatus) {
                    return { isCompleted: false, message: 'Please continue to click on <strong>Check crawl status</strong> until it completes successfully.' }
                }
                return { isCompleted: true };
            },
        },
        {
            element: '#seo-tabs-tab-graph',
            intro: 'Next, click on <strong>Content Graph</strong> to load a visualization of all your website blog content',
            position: 'right',
            disableInteraction: true,
            onBeforeNextStep: () => {
                // eslint-disable-next-line no-underscore-dangle
                const direction = (window as any).introContext._direction;
                if (direction === 'forward') {
                    document.getElementById('seo-tabs-tab-graph')!.click();
                    setTimeout(() => {
                        (window as any).introContext.refresh();
                    }, delayMs);
                }
            }
        },
        {
            // element: '.content-graph',
            tooltipClass: 'lg',
            intro: `
            <p>This is a visualization of all of the content on your website. </p>
            <div class="alert alert-info">Hover over any bubble to see: 
                <ul>
                    <li>what article it is, </li>
                    <li>the inbound links to it, and</li>
                    <li>click on it to navigate to the article directly in a new tab.</li>
                </ul>
            </div>
            `,
            // position: 'top'
        },
        {
            element: '#searchTerms',
            tooltipClass: 'lg',
            intro: `<p>Try searching for <code>keywords</code> to view related articles!</p>
            <div class="alert alert-info">This is a great way to explore how search engines and LLMs might index your content or see LSI keywords in relation to it.</div>`,
            position: 'bottom'
        },
        {
            tooltipClass: 'lg',
            intro: `
            <img src="/topic-authority-starter.gif" alt="Success!" style="margin:0 auto; display: block" />
            Now that we have website content indexed let's start building topic authority!
            `,
        }
    ]
}

const Step3GenerateTopicIdeas: SinglePageGuideCollection = {
    stageNumber: 3,
    cookieName: 'topic-authority-guide-stage-3',
    navigateTo: '/documents',
    steps: [
        {
            tooltipClass: 'lg',
            intro: `
            <img src="/whizzandra-in-progress-v2.gif" alt="almost there!" style="margin:0 auto; display: block" />
            <p>Now that you've completed all that <strong style="color: green">one-time setup work</strong>, generating new topic ideas is a breeze!</p>`,
            position: 'top',
            onBeforeNextStep: () => {
                const addNewBtn = document.getElementById('bg-nested-dropdown');
                const ddl = document.getElementsByClassName('dropdown-menu');
                // const ddlGrp = document.getElementsByClassName('dropdown-menu');
                // ddlGrp[0].className += " show";
                if (!ddl || ddl.length === 0 || !ddl[0].className.includes('show')) {
                    addNewBtn!.click();
                }
                setTimeout(() => {
                    (window as any).introContext.refresh();
                }, delayMs);
                // dropdown-menu show
            },
        },
        {
            element: '#bg-nested-dropdown',
            intro: 'Click on <strong>Add New...</strong> → <strong>Generate Topics</strong>!', // → <strong>Generate Topics</strong>
            position: 'top',
            disableInteraction: true,
            onBeforeNextStep: () => {
                const genTopicsBtn = document.getElementById('generateTopics');
                const modalWindow = document.getElementById('modalGenerateTopics');
                if (!modalWindow) {
                    genTopicsBtn?.click();
                }
            },
        },
        {
            title: 'That\'s it, congratulations!',
            tooltipClass: 'xl',
            intro: `
            <img src="whizzandra-dance-v1.gif" alt="celebrate!" style="margin:0 auto; display: block" />
            <p>Keep in mind that generating topic ideas takes <strong>up to 4 minutes</strong> to complete.</p>  
            Please <strong>be patient</strong>, triggering multiple times will not complete it faster.</p>
            <div class="alert alert-info"><strong>Remember</strong>: when you're ready to generate new topics, just click <strong>Add New...</strong> → <strong>Generate Topics</strong>!</p>`
        }
    ]
}

export const TopicAuthorityGuideKey = "topic-authority-guide-1";

const topicAuthorityGuidesCollection: MultiPageGuideCollection = {
    title: 'Team Efficiency Guide',
    key: TopicAuthorityGuideKey,
    steps: [
        Step1ConfigureProject,
        Step2UploadSitemap,
        Step3GenerateTopicIdeas
    ]
};

export default topicAuthorityGuidesCollection